import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { useDeferredReady } from './use-deferred-ready';
import { freshAdminAccessToken } from '../lib/token-refresh';

// ── Types ────────────────────────────────────────────────────────────

export interface Conversation {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  archived: boolean;
  messageCount: number;
}

export interface Message {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

export interface ConversationWithMessages extends Conversation {
  messages: Message[];
}

export interface AiSuggestion {
  id: string;
  organizationId: string;
  loadId: string | null;
  suggestionType: 'route_optimize' | 'driver_assign' | 'equipment_match' | 'schedule' | 'consolidate' | 'fuel_optimize';
  title: string;
  description: string;
  reasoning: string;
  confidenceScore: string | number;
  suggestedAction: Record<string, unknown>;
  status: 'pending' | 'accepted' | 'dismissed' | 'expired';
  acceptedBy: string | null;
  createdAt: string;
  updatedAt: string;
  load?: {
    id: string;
    displayId: string;
    referenceNumber: string;
    status: string;
  };
}

export interface CustomReport {
  id: string;
  name: string;
  description: string;
  config: ReportConfig;
  createdAt: string;
  updatedAt: string;
}

export interface ReportConfig {
  type: 'bar' | 'line' | 'pie' | 'area';
  query: string;
  xKey: string;
  yKeys: string[];
  title: string;
}

export interface ReportResult {
  data: Record<string, unknown>[];
  config: ReportConfig;
}

// ── Query Keys ───────────────────────────────────────────────────────

const aiKeys = {
  conversations: ['ai', 'conversations'] as const,
  conversation: (id: string) => ['ai', 'conversations', id] as const,
  suggestions: (filters?: Record<string, unknown>) =>
    ['ai', 'suggestions', filters ?? {}] as const,
  customReports: ['ai', 'custom-reports'] as const,
  reportResult: (id: string) => ['ai', 'custom-reports', id, 'result'] as const,
};

// ── Conversation Hooks ───────────────────────────────────────────────

export function useConversations() {
  return useQuery({
    queryKey: aiKeys.conversations,
    queryFn: async (): Promise<Conversation[]> => {
      const res = await api.get<{ data: Conversation[] }>('/ai/conversations');
      return res.data.data;
    },
  });
}

export function useConversation(id: string) {
  return useQuery({
    queryKey: aiKeys.conversation(id),
    queryFn: async (): Promise<ConversationWithMessages> => {
      const res = await api.get<{ data: ConversationWithMessages }>(
        `/ai/conversations/${id}`
      );
      return res.data.data;
    },
    enabled: !!id,
  });
}

export function useCreateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data?: {
      title?: string;
      contextType?: 'general' | 'dispatch' | 'reports' | 'load_detail';
    }): Promise<Conversation> => {
      const res = await api.post<{ data: Conversation }>(
        '/ai/conversations',
        data ?? {}
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.conversations });
    },
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      conversationId: string;
      content: string;
      context?: string;
    }): Promise<Message> => {
      const res = await api.post<{ data: Message }>(
        `/ai/conversations/${data.conversationId}/messages`,
        { content: data.content, context: data.context }
      );
      return res.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: aiKeys.conversation(variables.conversationId),
      });
    },
  });
}

export function useArchiveConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await api.patch(`/ai/conversations/${id}/archive`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.conversations });
    },
  });
}

// ── Streaming helper ─────────────────────────────────────────────────

const API_BASE_URL =
  (import.meta as unknown as Record<string, Record<string, string>>).env
    ?.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';

export async function streamMessage(
  conversationId: string,
  content: string,
  context: string | undefined,
  onChunk: (text: string) => void,
  onDone: () => void,
  onError: (err: Error) => void
) {
  // ATL-001144: raw fetch bypasses the axios renewal — renew a stale token first.
  const token = await freshAdminAccessToken();
  try {
    const res = await fetch(
      `${API_BASE_URL}/ai/conversations/${conversationId}/messages/stream`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content, context }),
      }
    );

    if (!res.ok) {
      throw new Error(`Stream request failed: ${res.status}`);
    }

    const reader = res.body?.getReader();
    if (!reader) {
      throw new Error('No readable stream');
    }

    const decoder = new TextDecoder();
    let done = false;

    while (!done) {
      const result = await reader.read();
      done = result.done;
      if (result.value) {
        const text = decoder.decode(result.value, { stream: true });
        // Parse SSE data lines
        const lines = text.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const payload = line.slice(6);
            if (payload === '[DONE]') {
              done = true;
              break;
            }
            try {
              const parsed = JSON.parse(payload);
              if (parsed.type === 'done') {
                done = true;
                break;
              }
              // Server sends { type: 'chunk', text: '...' }
              if (parsed.text) {
                onChunk(parsed.text);
              } else if (parsed.content) {
                onChunk(parsed.content);
              }
            } catch {
              // Raw text chunk
              onChunk(payload);
            }
          }
        }
      }
    }
    onDone();
  } catch (err) {
    onError(err instanceof Error ? err : new Error(String(err)));
  }
}

// ── AI Status Hook ───────────────────────────────────────────────────

/**
 * Returns whether the Anthropic API key is configured on the server.
 * Uses the /api/v1/ai/status endpoint (auth required).
 * The key itself is never returned — only a boolean.
 */
export function useAiStatus() {
  // #1429: non-critical shell data — defer off the cold-load critical path.
  const deferredReady = useDeferredReady();
  return useQuery({
    queryKey: ['ai', 'status'] as const,
    queryFn: async (): Promise<{ available: boolean; model: string | null }> => {
      const res = await api.get<{ data: { available: boolean; model: string | null } }>(
        '/ai/status'
      );
      return res.data.data;
    },
    enabled: deferredReady,
    staleTime: 60_000, // Re-check at most once per minute
  });
}

// ── Suggestion Hooks ─────────────────────────────────────────────────

export function useAiSuggestions(filters?: Record<string, unknown>) {
  return useQuery({
    queryKey: aiKeys.suggestions(filters),
    queryFn: async (): Promise<AiSuggestion[]> => {
      const res = await api.get<{ data: AiSuggestion[] }>(
        '/ai/suggestions',
        { params: filters }
      );
      return res.data.data;
    },
  });
}

export function useAcceptSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<AiSuggestion> => {
      const res = await api.post<{ data: AiSuggestion }>(`/ai/suggestions/${id}/accept`);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai', 'suggestions'] });
      queryClient.invalidateQueries({ queryKey: ['loads'] });
    },
  });
}

export function useDismissSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await api.post(`/ai/suggestions/${id}/dismiss`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai', 'suggestions'] });
    },
  });
}

export function useGenerateSuggestions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (loadId: string): Promise<AiSuggestion[]> => {
      const res = await api.post<{ data: AiSuggestion[] }>(
        `/ai/suggestions/generate`,
        { loadId }
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai', 'suggestions'] });
    },
  });
}

// ── Custom Reports Hooks ─────────────────────────────────────────────

export function useCustomReports() {
  return useQuery({
    queryKey: aiKeys.customReports,
    queryFn: async (): Promise<CustomReport[]> => {
      const res = await api.get<{ data: CustomReport[] }>('/ai/reports');
      return res.data.data;
    },
  });
}

export function useRunReport(reportId: string) {
  return useQuery({
    queryKey: aiKeys.reportResult(reportId),
    queryFn: async (): Promise<ReportResult> => {
      const res = await api.post<{ data: ReportResult }>(
        `/ai/reports/${reportId}/run`
      );
      return res.data.data;
    },
    enabled: !!reportId,
  });
}

// ── Dispatch AI Insights Hook ─────────────────────────────────────────

export interface DispatchInsight {
  id: string;
  title: string;
  detail: string;
  category: 'consolidation' | 'driver' | 'route' | 'hazmat' | 'cost' | 'general';
  /** Structured fields from the JSON reply (the optimizer prompt asks for them). */
  loadId?: string;
  driverId?: string;
  action?: string;
  savings?: number;
}

/**
 * ATL-000661/1 — a dispatch/route analysis runs a multi-turn tool loop on the
 * server (up to 5 provider round-trips plus tool execution), which routinely
 * outlives the shared 30s axios default. When the client aborted mid-flight the
 * optimizer panel read it as a dead analysis and sat on "Analyzing…".
 */
const AI_INSIGHTS_TIMEOUT_MS = 120_000;

/** Worth a second attempt: provider/gateway 5xx and client-side timeouts. */
function isTransientAiFailure(err: unknown): boolean {
  const e = err as { code?: string; response?: { status?: number } };
  if (e?.code === 'ECONNABORTED' || e?.code === 'ETIMEDOUT') return true;
  const status = e?.response?.status;
  return typeof status === 'number' && status >= 500;
}

export function useDispatchAiInsights() {
  return useMutation({
    mutationFn: async (payload: {
      prompt: string;
    }): Promise<DispatchInsight[]> => {
      // Create a temporary conversation for dispatch analysis, then send the
      // prompt. ATL-000661/1 — the FIRST analysis after a page refresh hit a
      // cold provider and came back 500 while the second click sailed through,
      // so one transparent retry is what the operator would have done by hand.
      const runOnce = async (): Promise<string> => {
        const convRes = await api.post<{ data: Conversation }>('/ai/conversations', {
          title: 'Dispatch Analysis',
          contextType: 'dispatch' as const,
        });
        const conversationId = convRes.data.data.id;

        const msgRes = await api.post<{ data: Message }>(
          `/ai/conversations/${conversationId}/messages`,
          { content: payload.prompt },
          { timeout: AI_INSIGHTS_TIMEOUT_MS }
        );
        return msgRes.data.data.content;
      };

      let content: string;
      try {
        content = await runOnce();
      } catch (err) {
        if (!isTransientAiFailure(err)) throw err;
        content = await runOnce();
      }

      // Route Optimizer QA — an empty reply (or empty JSON items) used to come
      // back as a blank "AI Analysis" card. Nothing to say → no cards.
      if (!content || !content.trim()) return [];

      // Try to parse structured insights from AI response
      try {
        const jsonMatch = content.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          if (Array.isArray(parsed)) {
            return parsed
              .map((item: any, idx: number): DispatchInsight => {
                const savings = Number(item?.savings);
                return {
                  id: `ai-insight-${idx}-${Date.now()}`,
                  title: String(item?.title ?? '').trim(),
                  detail: String(item?.detail || item?.description || item?.reasoning || '').trim(),
                  category: item?.category || 'general',
                  // The prompt asks for these — pass them through instead of
                  // making the panel regex them back out of the prose.
                  loadId: typeof item?.loadId === 'string' && item.loadId ? item.loadId : undefined,
                  driverId: typeof item?.driverId === 'string' && item.driverId ? item.driverId : undefined,
                  action: typeof item?.action === 'string' ? item.action : undefined,
                  savings: Number.isFinite(savings) && savings > 0 ? savings : undefined,
                };
              })
              .filter((i: DispatchInsight) => i.title || i.detail);
          }
        }
      } catch {
        // Fallback: parse free-text response into insights
      }

      // If JSON parsing fails, split the response into paragraphs as insights
      const paragraphs = content
        .split(/\n\n+/)
        .map((p: string) => p.trim())
        .filter((p: string) => p.length > 20);

      if (paragraphs.length === 0) {
        const text = content.trim();
        // A reply too short to be a paragraph is still an answer — but only
        // if there is something in it. (Blank `title` → the panel's
        // translated "AI Insight" label.)
        return text.length > 0
          ? [{ id: `ai-insight-0-${Date.now()}`, title: '', detail: text, category: 'general' as const }]
          : [];
      }

      return paragraphs.slice(0, 8).map((p: string, idx: number) => {
        // Try to extract a title from numbered lines or bold text
        const titleMatch = p.match(/^(?:\d+\.\s*)?(?:\*\*)?([^*\n:]{5,60})(?:\*\*)?[:\n]/);
        const title = titleMatch ? titleMatch[1].trim() : '';
        const detail = titleMatch ? p.slice(titleMatch[0].length).trim() : p;
        return {
          id: `ai-insight-${idx}-${Date.now()}`,
          title,
          detail,
          category: 'general' as const,
        };
      });
    },
  });
}

export function useCreateReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      description: string;
      config: ReportConfig;
    }): Promise<CustomReport> => {
      const res = await api.post<{ data: CustomReport }>('/ai/reports', data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.customReports });
    },
  });
}
